import { c as _c } from "react/compiler-runtime";
import { Button } from "@repo/ui/components/ui/button";
import { Input } from "@repo/ui/components/ui/input";
import { Label } from "@repo/ui/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/ui/components/ui/select";
import { Textarea } from "@repo/ui/components/ui/textarea";
import { useQuery } from "@tanstack/react-query";
import { Accessibility, ArrowLeft, ArrowUpDown, Camera, Car, CheckCircle2, DoorOpen, Loader2, MapPin, MoreHorizontal, Plus, Search, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ImageLightbox } from "@/lib/components/ImageLightbox";
import { PinPicker } from "@/lib/components/map/PinPicker";
import { FEATURE_CATEGORIES, useBidra } from "@/lib/hooks/useBidra";
import { trpc } from "@/router";

// ─── Main view ──────────────────────────────────────────────────────────────
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function BidraView() {
  const $ = _c(73);
  const b = useBidra();
  let t0;
  if ($[0] !== b.handleBack || $[1] !== b.step) {
    t0 = b.step !== "done" && b.step !== "submitting" && /*#__PURE__*/_jsxs("button", {
      onClick: b.handleBack,
      className: "mb-4 flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700",
      children: [/*#__PURE__*/_jsx(ArrowLeft, {
        className: "h-4 w-4"
      }), "Tillbaka"]
    });
    $[0] = b.handleBack;
    $[1] = b.step;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const t1 = b.step === "done" ? "Tack!" : b.step === "new-place" ? "L\xE4gg till ny plats" : b.step === "feature-type" ? "Vad vill du rapportera?" : b.step === "pin-feature" ? "Var finns den?" : "Bidra";
  let t2;
  if ($[3] !== t1) {
    t2 = /*#__PURE__*/_jsx("h1", {
      className: "mb-1 text-xl font-bold text-gray-900",
      children: t1
    });
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== b.categoryConfig || $[6] !== b.step) {
    t3 = b.step !== "done" && b.step !== "submitting" && /*#__PURE__*/_jsx("p", {
      className: "mb-6 text-sm text-gray-500",
      children: b.step === "place" ? "V\xE4lj en plats eller l\xE4gg till en ny" : b.step === "new-place" ? "Beskriv platsen du vill l\xE4gga till" : b.step === "feature-type" ? "V\xE4lj vad du vill bidra med" : b.step === "pin-feature" ? "Dra kartan s\xE5 att n\xE5len pekar r\xE4tt" : b.categoryConfig ? `Ta en bild — ${b.categoryConfig.label.toLowerCase()}` : "Ta en bild"
    });
    $[5] = b.categoryConfig;
    $[6] = b.step;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== b.error) {
    t4 = b.error && /*#__PURE__*/_jsx("div", {
      className: "mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700",
      children: b.error
    });
    $[8] = b.error;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== b.goToNewPlace || $[11] !== b.selectPlace || $[12] !== b.step) {
    t5 = b.step === "place" && /*#__PURE__*/_jsx(StepPlace, {
      onSelectPlace: b.selectPlace,
      onAddNew: b.goToNewPlace
    });
    $[10] = b.goToNewPlace;
    $[11] = b.selectPlace;
    $[12] = b.step;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== b.childCategories || $[15] !== b.continueFromNewPlace || $[16] !== b.isNewPlaceValid || $[17] !== b.newPlaceCategoryId || $[18] !== b.newPlaceName || $[19] !== b.parentCategories || $[20] !== b.pinLat || $[21] !== b.pinLng || $[22] !== b.setNewPlaceCategoryId || $[23] !== b.setNewPlaceName || $[24] !== b.setPinLocation || $[25] !== b.step) {
    t6 = b.step === "new-place" && /*#__PURE__*/_jsx(StepNewPlace, {
      name: b.newPlaceName,
      onNameChange: b.setNewPlaceName,
      categoryId: b.newPlaceCategoryId,
      onCategoryChange: b.setNewPlaceCategoryId,
      latitude: b.pinLat,
      longitude: b.pinLng,
      onLocationChange: b.setPinLocation,
      parentCategories: b.parentCategories,
      childCategories: b.childCategories,
      onContinue: b.continueFromNewPlace,
      isValid: b.isNewPlaceValid
    });
    $[14] = b.childCategories;
    $[15] = b.continueFromNewPlace;
    $[16] = b.isNewPlaceValid;
    $[17] = b.newPlaceCategoryId;
    $[18] = b.newPlaceName;
    $[19] = b.parentCategories;
    $[20] = b.pinLat;
    $[21] = b.pinLng;
    $[22] = b.setNewPlaceCategoryId;
    $[23] = b.setNewPlaceName;
    $[24] = b.setPinLocation;
    $[25] = b.step;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== b.selectCategory || $[28] !== b.step) {
    t7 = b.step === "feature-type" && /*#__PURE__*/_jsx(StepFeatureType, {
      onSelect: b.selectCategory
    });
    $[27] = b.selectCategory;
    $[28] = b.step;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== b.categoryConfig?.photoTips || $[31] !== b.extraInfo || $[32] !== b.featureTypeId || $[33] !== b.featureTypes || $[34] !== b.handlePhotoContinue || $[35] !== b.isPhotoValid || $[36] !== b.newPlaceName || $[37] !== b.placeName || $[38] !== b.selectedPhoto || $[39] !== b.setExtraInfo || $[40] !== b.setFeatureTypeId || $[41] !== b.setSelectedPhoto || $[42] !== b.setShowExtraInfo || $[43] !== b.showExtraInfo || $[44] !== b.step) {
    t8 = b.step === "photo" && /*#__PURE__*/_jsx(StepPhoto, {
      photo: b.selectedPhoto,
      onPhotoSelect: b.setSelectedPhoto,
      featureTypeId: b.featureTypeId,
      onFeatureTypeChange: b.setFeatureTypeId,
      featureTypes: b.featureTypes,
      extraInfo: b.extraInfo,
      onExtraInfoChange: b.setExtraInfo,
      showExtraInfo: b.showExtraInfo,
      onShowExtraInfo: b.setShowExtraInfo,
      onContinue: b.handlePhotoContinue,
      isValid: b.isPhotoValid,
      placeName: b.placeName ?? b.newPlaceName,
      photoTips: b.categoryConfig?.photoTips
    });
    $[30] = b.categoryConfig?.photoTips;
    $[31] = b.extraInfo;
    $[32] = b.featureTypeId;
    $[33] = b.featureTypes;
    $[34] = b.handlePhotoContinue;
    $[35] = b.isPhotoValid;
    $[36] = b.newPlaceName;
    $[37] = b.placeName;
    $[38] = b.selectedPhoto;
    $[39] = b.setExtraInfo;
    $[40] = b.setFeatureTypeId;
    $[41] = b.setSelectedPhoto;
    $[42] = b.setShowExtraInfo;
    $[43] = b.showExtraInfo;
    $[44] = b.step;
    $[45] = t8;
  } else {
    t8 = $[45];
  }
  let t9;
  if ($[46] !== b.categoryConfig?.pinRequired || $[47] !== b.featureLat || $[48] !== b.featureLng || $[49] !== b.handleSubmit || $[50] !== b.isFeatureLocationValid || $[51] !== b.placeLocation || $[52] !== b.setFeatureLocation || $[53] !== b.step) {
    t9 = b.step === "pin-feature" && /*#__PURE__*/_jsx(StepPinEntrance, {
      latitude: b.featureLat,
      longitude: b.featureLng,
      placeLocation: b.placeLocation,
      onLocationChange: b.setFeatureLocation,
      onSubmit: b.handleSubmit,
      isValid: b.isFeatureLocationValid,
      pinRequired: b.categoryConfig?.pinRequired ?? false
    });
    $[46] = b.categoryConfig?.pinRequired;
    $[47] = b.featureLat;
    $[48] = b.featureLng;
    $[49] = b.handleSubmit;
    $[50] = b.isFeatureLocationValid;
    $[51] = b.placeLocation;
    $[52] = b.setFeatureLocation;
    $[53] = b.step;
    $[54] = t9;
  } else {
    t9 = $[54];
  }
  let t10;
  if ($[55] !== b.step) {
    t10 = b.step === "submitting" && /*#__PURE__*/_jsxs("div", {
      className: "flex flex-col items-center justify-center py-16",
      children: [/*#__PURE__*/_jsx(Loader2, {
        className: "mb-3 h-8 w-8 animate-spin text-brand"
      }), /*#__PURE__*/_jsx("p", {
        className: "text-sm text-gray-600",
        children: "Laddar upp och analyserar..."
      })]
    });
    $[55] = b.step;
    $[56] = t10;
  } else {
    t10 = $[56];
  }
  let t11;
  if ($[57] !== b.handleReset || $[58] !== b.navigateToKarta || $[59] !== b.step) {
    t11 = b.step === "done" && /*#__PURE__*/_jsx(StepDone, {
      onAnother: b.handleReset,
      onClose: b.navigateToKarta
    });
    $[57] = b.handleReset;
    $[58] = b.navigateToKarta;
    $[59] = b.step;
    $[60] = t11;
  } else {
    t11 = $[60];
  }
  let t12;
  if ($[61] !== t0 || $[62] !== t10 || $[63] !== t11 || $[64] !== t2 || $[65] !== t3 || $[66] !== t4 || $[67] !== t5 || $[68] !== t6 || $[69] !== t7 || $[70] !== t8 || $[71] !== t9) {
    t12 = /*#__PURE__*/_jsxs("div", {
      className: "mx-auto max-w-lg px-4 py-6 pb-[calc(env(safe-area-inset-bottom)+2rem)]",
      children: [t0, t2, t3, t4, t5, t6, t7, t8, t9, t10, t11]
    });
    $[61] = t0;
    $[62] = t10;
    $[63] = t11;
    $[64] = t2;
    $[65] = t3;
    $[66] = t4;
    $[67] = t5;
    $[68] = t6;
    $[69] = t7;
    $[70] = t8;
    $[71] = t9;
    $[72] = t12;
  } else {
    t12 = $[72];
  }
  return t12;
}

// ─── Step: Feature type selection ─────────────────────────────────────────────

const CATEGORY_ICONS = {
  "door-open": DoorOpen,
  car: Car,
  accessibility: Accessibility,
  "arrow-up-down": ArrowUpDown,
  "more-horizontal": MoreHorizontal
};
function StepFeatureType(t0) {
  const $ = _c(4);
  const {
    onSelect
  } = t0;
  let t1;
  if ($[0] !== onSelect) {
    t1 = FEATURE_CATEGORIES.map(cat => {
      const Icon = CATEGORY_ICONS[cat.icon] ?? MapPin;
      return /*#__PURE__*/_jsxs("button", {
        onClick: () => onSelect(cat.id),
        className: "flex flex-col items-center gap-2 rounded-xl border-2 border-gray-200 px-4 py-5 text-center transition-colors hover:border-brand hover:bg-brand/5",
        children: [/*#__PURE__*/_jsx("div", {
          className: "flex h-12 w-12 items-center justify-center rounded-full bg-gray-100",
          children: /*#__PURE__*/_jsx(Icon, {
            className: "h-6 w-6 text-gray-600"
          })
        }), /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx("p", {
            className: "text-sm font-semibold text-gray-900",
            children: cat.label
          }), /*#__PURE__*/_jsx("p", {
            className: "mt-0.5 text-xs text-gray-500",
            children: cat.description
          })]
        })]
      }, cat.id);
    });
    $[0] = onSelect;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = /*#__PURE__*/_jsx("div", {
      className: "grid grid-cols-2 gap-3",
      children: t1
    });
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}

// ─── Step: Place selection ────────────────────────────────────────────────────

function StepPlace(t0) {
  const $ = _c(31);
  const {
    onSelectPlace,
    onAddNew
  } = t0;
  const [query, setQuery] = useState("");
  let t1;
  if ($[0] !== query) {
    t1 = trpc.places.search.queryOptions({
      query,
      limit: 8
    });
    $[0] = query;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = query.length >= 2;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      ...t1,
      enabled: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: t4,
    isLoading
  } = useQuery(t3);
  let t5;
  if ($[5] !== t4) {
    t5 = t4 === undefined ? [] : t4;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const results = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = /*#__PURE__*/_jsx(Search, {
      className: "absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
    });
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = e => setQuery(e.target.value);
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== query) {
    t8 = /*#__PURE__*/_jsxs("div", {
      className: "relative",
      children: [t6, /*#__PURE__*/_jsx(Input, {
        placeholder: "S\xF6k plats...",
        value: query,
        onChange: t7,
        className: "h-11 pl-9 text-base",
        autoFocus: true
      })]
    });
    $[9] = query;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== isLoading || $[12] !== query.length) {
    t9 = isLoading && query.length >= 2 && /*#__PURE__*/_jsx("div", {
      className: "flex justify-center py-4",
      children: /*#__PURE__*/_jsx(Loader2, {
        className: "h-5 w-5 animate-spin text-gray-400"
      })
    });
    $[11] = isLoading;
    $[12] = query.length;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== onSelectPlace || $[15] !== results) {
    t10 = results.length > 0 && /*#__PURE__*/_jsx("div", {
      className: "space-y-1 rounded-lg border bg-white p-1.5",
      children: results.map(p => /*#__PURE__*/_jsxs("button", {
        onClick: () => onSelectPlace(p.id, p.name),
        className: "flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-gray-50",
        children: [/*#__PURE__*/_jsx(MapPin, {
          className: "h-4 w-4 shrink-0 text-gray-400"
        }), /*#__PURE__*/_jsx("span", {
          className: "truncate text-sm font-medium",
          children: p.name
        })]
      }, p.id))
    });
    $[14] = onSelectPlace;
    $[15] = results;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== isLoading || $[18] !== query.length || $[19] !== results.length) {
    t11 = query.length >= 2 && !isLoading && results.length === 0 && /*#__PURE__*/_jsx("p", {
      className: "py-2 text-center text-sm text-gray-500",
      children: "Inga platser hittades"
    });
    $[17] = isLoading;
    $[18] = query.length;
    $[19] = results.length;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = /*#__PURE__*/_jsx("div", {
      className: "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand/10",
      children: /*#__PURE__*/_jsx(Plus, {
        className: "h-4 w-4 text-brand"
      })
    });
    t13 = /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx("p", {
        className: "text-sm font-medium text-gray-900",
        children: "Hittar du inte platsen?"
      }), /*#__PURE__*/_jsx("p", {
        className: "text-xs text-gray-500",
        children: "L\xE4gg till en ny plats"
      })]
    });
    $[21] = t12;
    $[22] = t13;
  } else {
    t12 = $[21];
    t13 = $[22];
  }
  let t14;
  if ($[23] !== onAddNew) {
    t14 = /*#__PURE__*/_jsxs("button", {
      onClick: onAddNew,
      className: "flex w-full items-center gap-3 rounded-lg border-2 border-dashed border-gray-300 px-4 py-3.5 text-left transition-colors hover:border-brand hover:bg-brand/5",
      children: [t12, t13]
    });
    $[23] = onAddNew;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t14 || $[28] !== t8 || $[29] !== t9) {
    t15 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-4",
      children: [t8, t9, t10, t11, t14]
    });
    $[25] = t10;
    $[26] = t11;
    $[27] = t14;
    $[28] = t8;
    $[29] = t9;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}

// ─── Step: New place ──────────────────────────────────────────────────────────

function StepNewPlace(t0) {
  const $ = _c(34);
  const {
    name,
    onNameChange,
    categoryId,
    onCategoryChange,
    latitude,
    longitude,
    onLocationChange,
    parentCategories,
    childCategories,
    onContinue,
    isValid
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /*#__PURE__*/_jsxs(Label, {
      className: "text-sm font-medium",
      children: ["Namn ", /*#__PURE__*/_jsx("span", {
        className: "text-red-500",
        children: "*"
      })]
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onNameChange) {
    t2 = e => onNameChange(e.target.value);
    $[1] = onNameChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== name || $[4] !== t2) {
    t3 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-2",
      children: [t1, /*#__PURE__*/_jsx(Input, {
        value: name,
        onChange: t2,
        placeholder: "T.ex. Stadsbiblioteket",
        className: "h-11 text-base",
        autoFocus: true
      })]
    });
    $[3] = name;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = /*#__PURE__*/_jsxs(Label, {
      className: "text-sm font-medium",
      children: ["Kategori ", /*#__PURE__*/_jsx("span", {
        className: "text-red-500",
        children: "*"
      })]
    });
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = categoryId ?? "";
  let t6;
  if ($[7] !== onCategoryChange) {
    t6 = e_0 => onCategoryChange(e_0.target.value ? Number(e_0.target.value) : null);
    $[7] = onCategoryChange;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = /*#__PURE__*/_jsx("option", {
      value: "",
      children: "V\xE4lj kategori..."
    });
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== childCategories || $[11] !== parentCategories) {
    let t9;
    if ($[13] !== childCategories) {
      t9 = parent => /*#__PURE__*/_jsx("optgroup", {
        label: parent.name,
        children: childCategories.filter(c => c.parentId === parent.id).map(_temp)
      }, parent.id);
      $[13] = childCategories;
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    t8 = parentCategories.map(t9);
    $[10] = childCategories;
    $[11] = parentCategories;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[15] !== t5 || $[16] !== t6 || $[17] !== t8) {
    t9 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-2",
      children: [t4, /*#__PURE__*/_jsxs("select", {
        value: t5,
        onChange: t6,
        className: "border-input bg-background h-11 w-full rounded-md border px-3 text-base",
        children: [t7, t8]
      })]
    });
    $[15] = t5;
    $[16] = t6;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = /*#__PURE__*/_jsxs(Label, {
      className: "text-sm font-medium",
      children: ["Plats p\xE5 kartan ", /*#__PURE__*/_jsx("span", {
        className: "text-red-500",
        children: "*"
      })]
    });
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== onLocationChange) {
    t11 = (lat, lng) => onLocationChange(lat, lng);
    $[20] = onLocationChange;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== latitude || $[23] !== longitude || $[24] !== t11) {
    t12 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-2",
      children: [t10, /*#__PURE__*/_jsx("div", {
        className: "h-64 overflow-hidden rounded-lg border",
        children: /*#__PURE__*/_jsx(PinPicker, {
          latitude: latitude,
          longitude: longitude,
          onPick: t11
        })
      })]
    });
    $[22] = latitude;
    $[23] = longitude;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const t13 = !isValid;
  let t14;
  if ($[26] !== onContinue || $[27] !== t13) {
    t14 = /*#__PURE__*/_jsx(Button, {
      onClick: onContinue,
      disabled: t13,
      className: "h-11 w-full gap-2 bg-brand hover:bg-brand/90",
      children: "Forts\xE4tt till foto"
    });
    $[26] = onContinue;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t12 || $[30] !== t14 || $[31] !== t3 || $[32] !== t9) {
    t15 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-5",
      children: [t3, t9, t12, t14]
    });
    $[29] = t12;
    $[30] = t14;
    $[31] = t3;
    $[32] = t9;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  return t15;
}

// ─── Step: Photo + entrance type ──────────────────────────────────────────────
function _temp(child) {
  return /*#__PURE__*/_jsx("option", {
    value: child.id,
    children: child.name
  }, child.id);
}
function StepPhoto(t0) {
  const $ = _c(49);
  const {
    photo,
    onPhotoSelect,
    featureTypeId,
    onFeatureTypeChange,
    featureTypes,
    extraInfo,
    onExtraInfoChange,
    showExtraInfo,
    onShowExtraInfo,
    onContinue,
    isValid,
    placeName,
    photoTips
  } = t0;
  const inputRef = useRef(null);
  const [lightbox, setLightbox] = useState(false);
  let t1;
  bb0: {
    if (!photo) {
      t1 = null;
      break bb0;
    }
    let t2;
    if ($[0] !== photo) {
      t2 = URL.createObjectURL(photo);
      $[0] = photo;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    t1 = t2;
  }
  const previewUrl = t1;
  let t2;
  let t3;
  if ($[2] !== previewUrl) {
    t2 = () => () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
    t3 = [previewUrl];
    $[2] = previewUrl;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== placeName) {
    t4 = placeName && /*#__PURE__*/_jsxs("div", {
      className: "flex items-center gap-2 rounded-lg border border-brand/20 bg-brand/5 px-3 py-2",
      children: [/*#__PURE__*/_jsx(MapPin, {
        className: "h-3.5 w-3.5 text-brand"
      }), /*#__PURE__*/_jsx("span", {
        className: "text-sm font-medium text-gray-900",
        children: placeName
      })]
    });
    $[5] = placeName;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== photo || $[8] !== previewUrl) {
    t5 = photo ? /*#__PURE__*/_jsx("button", {
      type: "button",
      onClick: () => setLightbox(true),
      className: "h-36 w-36 overflow-hidden rounded-lg border transition-opacity hover:opacity-80",
      children: /*#__PURE__*/_jsx("img", {
        src: previewUrl,
        alt: "Vald bild",
        className: "h-full w-full object-cover"
      })
    }) : /*#__PURE__*/_jsx("button", {
      type: "button",
      onClick: () => inputRef.current?.click(),
      className: "flex h-36 w-36 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 transition-colors hover:border-brand",
      children: /*#__PURE__*/_jsxs("div", {
        className: "flex flex-col items-center gap-1 text-center",
        children: [/*#__PURE__*/_jsx(Camera, {
          className: "h-8 w-8 text-gray-400"
        }), /*#__PURE__*/_jsx("p", {
          className: "text-xs text-gray-400",
          children: "Ta bild"
        })]
      })
    });
    $[7] = photo;
    $[8] = previewUrl;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== photo) {
    t6 = photo && /*#__PURE__*/_jsx("button", {
      type: "button",
      onClick: () => inputRef.current?.click(),
      className: "mt-1.5 text-xs text-gray-500 hover:text-gray-700",
      children: "Byt bild"
    });
    $[10] = photo;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = /*#__PURE__*/_jsxs("div", {
      className: "flex w-36 shrink-0 flex-col items-center",
      children: [t5, t6]
    });
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = previewUrl ?? "";
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setLightbox(false);
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== lightbox || $[17] !== t8) {
    t10 = /*#__PURE__*/_jsx(ImageLightbox, {
      src: t8,
      alt: "F\xF6rhandsvisning",
      open: lightbox,
      onClose: t9
    });
    $[16] = lightbox;
    $[17] = t8;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== featureTypeId || $[20] !== featureTypes || $[21] !== onFeatureTypeChange) {
    t11 = featureTypes.length > 1 && /*#__PURE__*/_jsxs("div", {
      className: "space-y-1.5",
      children: [/*#__PURE__*/_jsx(Label, {
        className: "text-xs font-medium text-gray-500",
        children: "Typ"
      }), /*#__PURE__*/_jsxs(Select, {
        value: featureTypeId?.toString() ?? undefined,
        onValueChange: v => onFeatureTypeChange(parseInt(v)),
        children: [/*#__PURE__*/_jsx(SelectTrigger, {
          className: "h-9 text-sm",
          children: /*#__PURE__*/_jsx(SelectValue, {
            placeholder: "V\xE4lj typ"
          })
        }), /*#__PURE__*/_jsx(SelectContent, {
          children: featureTypes.map(_temp2)
        })]
      })]
    });
    $[19] = featureTypeId;
    $[20] = featureTypes;
    $[21] = onFeatureTypeChange;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== extraInfo || $[24] !== onExtraInfoChange || $[25] !== onShowExtraInfo || $[26] !== showExtraInfo) {
    t12 = showExtraInfo ? /*#__PURE__*/_jsx(Textarea, {
      value: extraInfo,
      onChange: e => onExtraInfoChange(e.target.value),
      placeholder: "T.ex. 'Entr\xE9n ligger p\xE5 baksidan'",
      className: "h-16 resize-none text-sm",
      autoFocus: true
    }) : /*#__PURE__*/_jsxs("button", {
      type: "button",
      onClick: onShowExtraInfo,
      className: "inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700",
      children: [/*#__PURE__*/_jsx("span", {
        children: "+"
      }), " Kommentar"]
    });
    $[23] = extraInfo;
    $[24] = onExtraInfoChange;
    $[25] = onShowExtraInfo;
    $[26] = showExtraInfo;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t11 || $[29] !== t12) {
    t13 = /*#__PURE__*/_jsxs("div", {
      className: "min-w-0 flex-1 space-y-3",
      children: [t11, t12]
    });
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t10 || $[32] !== t13 || $[33] !== t7) {
    t14 = /*#__PURE__*/_jsxs("div", {
      className: "flex gap-4",
      children: [t7, t10, t13]
    });
    $[31] = t10;
    $[32] = t13;
    $[33] = t7;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== onPhotoSelect) {
    t15 = /*#__PURE__*/_jsx("input", {
      ref: inputRef,
      type: "file",
      accept: "image/*",
      className: "hidden",
      onChange: e_0 => {
        const f = e_0.target.files?.[0];
        if (f) {
          onPhotoSelect(f);
        }
      }
    });
    $[35] = onPhotoSelect;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== photo || $[38] !== photoTips) {
    t16 = !photo && photoTips && photoTips.length > 0 && /*#__PURE__*/_jsxs("div", {
      className: "rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5",
      children: [/*#__PURE__*/_jsx("p", {
        className: "text-xs font-semibold text-amber-800",
        children: "Tips f\xF6r bra bild"
      }), /*#__PURE__*/_jsx("ul", {
        className: "mt-1 space-y-0.5 text-xs text-amber-700",
        children: photoTips.map(_temp3)
      })]
    });
    $[37] = photo;
    $[38] = photoTips;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const t17 = !isValid;
  let t18;
  if ($[40] !== onContinue || $[41] !== t17) {
    t18 = /*#__PURE__*/_jsx(Button, {
      onClick: onContinue,
      disabled: t17,
      className: "h-11 w-full gap-2 bg-brand hover:bg-brand/90",
      children: "Forts\xE4tt"
    });
    $[40] = onContinue;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t14 || $[44] !== t15 || $[45] !== t16 || $[46] !== t18 || $[47] !== t4) {
    t19 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-4",
      children: [t4, t14, t15, t16, t18]
    });
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
    $[46] = t18;
    $[47] = t4;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  return t19;
}

// ─── Step: Pin feature location ───────────────────────────────────────────────
function _temp3(tip, i) {
  return /*#__PURE__*/_jsxs("li", {
    children: ["\u2022 ", tip]
  }, i);
}
function _temp2(t) {
  return /*#__PURE__*/_jsx(SelectItem, {
    value: t.id.toString(),
    children: t.name
  }, t.id);
}
function StepPinEntrance(t0) {
  const $ = _c(20);
  const {
    latitude,
    longitude,
    placeLocation,
    onLocationChange,
    onSubmit,
    isValid,
    pinRequired
  } = t0;
  let t1;
  if ($[0] !== placeLocation) {
    t1 = placeLocation && /*#__PURE__*/_jsxs("div", {
      className: "flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500",
      children: [/*#__PURE__*/_jsx(MapPin, {
        className: "h-3.5 w-3.5"
      }), "Bl\xE5 mark\xF6r visar platsen \u2014 flytta kartan till r\xE4tt position"]
    });
    $[0] = placeLocation;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onLocationChange) {
    t2 = (lat, lng) => onLocationChange(lat, lng);
    $[2] = onLocationChange;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = placeLocation ?? undefined;
  let t4;
  if ($[4] !== latitude || $[5] !== longitude || $[6] !== t2 || $[7] !== t3) {
    t4 = /*#__PURE__*/_jsx("div", {
      className: "h-72 overflow-hidden rounded-lg border",
      children: /*#__PURE__*/_jsx(PinPicker, {
        latitude: latitude,
        longitude: longitude,
        zoom: 19,
        onPick: t2,
        placeMarker: t3
      })
    });
    $[4] = latitude;
    $[5] = longitude;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = pinRequired && !isValid;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = /*#__PURE__*/_jsx(Upload, {
      className: "h-4 w-4"
    });
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== onSubmit || $[11] !== t5) {
    t7 = /*#__PURE__*/_jsxs(Button, {
      onClick: onSubmit,
      disabled: t5,
      className: "h-11 w-full gap-2 bg-brand hover:bg-brand/90",
      children: [t6, "Skicka in"]
    });
    $[10] = onSubmit;
    $[11] = t5;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== pinRequired) {
    t8 = !pinRequired && /*#__PURE__*/_jsx("p", {
      className: "text-center text-xs text-gray-400",
      children: "Markering \xE4r valfri \u2014 du kan skicka in direkt"
    });
    $[13] = pinRequired;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t1 || $[16] !== t4 || $[17] !== t7 || $[18] !== t8) {
    t9 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-4",
      children: [t1, t4, t7, t8]
    });
    $[15] = t1;
    $[16] = t4;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}

// ─── Step: Done ─────────────────────────────────────────────────────────────

function StepDone(t0) {
  const $ = _c(8);
  const {
    onAnother,
    onClose
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /*#__PURE__*/_jsxs("div", {
      className: "mb-6 flex items-center gap-3",
      children: [/*#__PURE__*/_jsx("div", {
        className: "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100",
        children: /*#__PURE__*/_jsx(CheckCircle2, {
          className: "h-5 w-5 text-green-600"
        })
      }), /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx("h3", {
          className: "text-lg font-bold text-gray-900",
          children: "Tack f\xF6r ditt bidrag!"
        }), /*#__PURE__*/_jsx("p", {
          className: "text-sm text-gray-500",
          children: "Din rapport granskas och publiceras snart."
        })]
      })]
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onAnother) {
    t2 = /*#__PURE__*/_jsx(Button, {
      variant: "outline",
      onClick: onAnother,
      className: "h-11 flex-1",
      children: "L\xE4gg till ytterligare"
    });
    $[1] = onAnother;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onClose) {
    t3 = /*#__PURE__*/_jsx(Button, {
      onClick: onClose,
      className: "h-11 flex-1 bg-brand hover:bg-brand/90",
      children: "Tillbaka till kartan"
    });
    $[3] = onClose;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = /*#__PURE__*/_jsxs("div", {
      className: "py-4",
      children: [t1, /*#__PURE__*/_jsxs("div", {
        className: "flex gap-2",
        children: [t2, t3]
      })]
    });
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}